import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SEOHead } from "@/components/SEOHead";
import logoFreelaDash from "@/assets/logo-freeladash.png";

type PageState = "form" | "success" | "invalid_token";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pageState, setPageState] = useState<PageState>(token ? "form" : "invalid_token");
  const [errorMessage, setErrorMessage] = useState<string>(
    token ? "" : "Link de recuperação inválido. Solicite um novo link."
  );

  // Limpar timer ao desmontar para evitar memory leak
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  // Validações de senha
  const passwordValidations = {
    minLength: newPassword.length >= 6,
    matching: newPassword === confirmPassword && confirmPassword.length > 0,
  };

  const isFormValid = passwordValidations.minLength && passwordValidations.matching;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setPageState("invalid_token");
      setErrorMessage("Token inválido. Solicite um novo link de recuperação.");
      return;
    }

    if (!isFormValid) {
      toast.error("Por favor, corrija os erros no formulário");
      return;
    }

    setIsLoading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("reset-password", {
        body: { token, newPassword }
      });

      if (fnError) {
        console.error("Error resetting password:", fnError);
        // Erro de rede/servidor - permitir que o usuário tente novamente
        toast.error("Erro de conexão. Verifique sua internet e tente novamente.");
        return;
      }

      if (!data?.success) {
        // Erro do token (expirado, já usado, inválido)
        setPageState("invalid_token");
        setErrorMessage(data?.error || "Token inválido ou expirado. Solicite um novo link.");
        return;
      }

      setPageState("success");
      toast.success("Senha alterada com sucesso!");
      
      // Redirecionar para login após 3 segundos (com cleanup)
      redirectTimerRef.current = setTimeout(() => {
        navigate("/login", { 
          state: { message: "Senha alterada com sucesso! Faça login com sua nova senha." }
        });
      }, 3000);

    } catch (error) {
      console.error("Error:", error);
      // Erro de rede - não mostrar como token inválido
      toast.error("Erro de conexão. Verifique sua internet e tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const LogoHeader = () => (
    <Link to="/" className="mx-auto mb-4">
      <img 
        src={logoFreelaDash} 
        alt="FreelaDash" 
        className="h-16 w-16 rounded-xl object-contain"
      />
    </Link>
  );

  // Tela de erro (token inválido)
  if (pageState === "invalid_token") {
    return (
      <>
        <SEOHead 
          title="Link Inválido"
          description="O link de recuperação de senha é inválido ou expirou."
          canonical="/reset-password"
        />
        <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
          <Card className="w-full max-w-md shadow-card-lg animate-fade-in">
            <CardHeader className="text-center">
              <LogoHeader />
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 mb-4">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle className="text-2xl">Link inválido</CardTitle>
              <CardDescription className="text-base mt-2">
                {errorMessage}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200">
                <p className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Os links de recuperação expiram em 1 hora e só podem ser usados uma vez.</span>
                </p>
              </div>
              <Button className="w-full" asChild>
                <Link to="/forgot-password">Solicitar novo link</Link>
              </Button>
            </CardContent>
            <CardFooter className="justify-center">
              <Link
                to="/login"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar para o login
              </Link>
            </CardFooter>
          </Card>
        </div>
      </>
    );
  }

  // Tela de sucesso
  if (pageState === "success") {
    return (
      <>
        <SEOHead 
          title="Senha Alterada"
          description="Sua senha foi alterada com sucesso."
          canonical="/reset-password"
        />
        <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
          <Card className="w-full max-w-md shadow-card-lg animate-fade-in">
            <CardHeader className="text-center">
              <LogoHeader />
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Senha alterada!</CardTitle>
              <CardDescription className="text-base mt-2">
                Sua senha foi alterada com sucesso. Você será redirecionado para a página de login.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" asChild>
                <Link to="/login">Ir para o login</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Formulário de redefinição
  return (
    <>
      <SEOHead 
        title="Redefinir Senha"
        description="Redefina sua senha do FreelaDash."
        canonical="/reset-password"
      />
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md shadow-card-lg animate-fade-in">
          <CardHeader className="text-center">
            <LogoHeader />
            <CardTitle className="text-2xl">Redefinir senha</CardTitle>
            <CardDescription>
              Digite sua nova senha
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nova senha</Label>
                <div className="relative">
                  <Input 
                    id="newPassword" 
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="new-password"
                    autoFocus
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar senha</Label>
                <div className="relative">
                  <Input 
                    id="confirmPassword" 
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Validações visuais */}
              <div className="space-y-2 text-sm">
                <div className={`flex items-center gap-2 ${passwordValidations.minLength ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {passwordValidations.minLength ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-current" />
                  )}
                  <span>Mínimo de 6 caracteres</span>
                </div>
                <div className={`flex items-center gap-2 ${passwordValidations.matching ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {passwordValidations.matching ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-current" />
                  )}
                  <span>Senhas coincidem</span>
                </div>
              </div>

              <Button 
                className="w-full" 
                type="submit" 
                disabled={isLoading || !isFormValid}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Alterando senha...
                  </>
                ) : (
                  "Alterar senha"
                )}
              </Button>
            </CardContent>
          </form>
          <CardFooter className="justify-center">
            <Link
              to="/login"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para o login
            </Link>
          </CardFooter>
        </Card>
      </div>
    </>
  );
}
